import { type SessionSummaryQuery } from "@crowdcoursing/api/graphql";
import { type Theme } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo } from "react";

type EventFromQuery = NonNullable<SessionSummaryQuery["session"]>;

interface CalendarEvent {
  color: string;
  endTime: string;
  id: string;
  isSelected: boolean;
  isUserAttending: boolean;
  startTime: string;
  title: string;
}

interface UseEventCalendarProps {
  event: EventFromQuery;
  onRSVP?: (sessionId: string) => void;
  onCancelRSVP?: (sessionId: string) => void;
  onSelectMeetingTime?: (meetingTime: { id: string; startTime: string; endTime: string } | undefined) => void;
  selectedMeetingTimeId?: string;
  selectedDate?: DateTime;
  onSelectDate?: (date: DateTime | undefined) => void;
  currentMonth: DateTime;
  onMonthChange: (month: DateTime) => void;
  theme: Theme;
}

interface UseEventCalendarReturn {
  calendarEvents: CalendarEvent[];
  handlePrevious: () => void;
  handleNext: () => void;
  handleToday: () => void;
  handleEventClick: (e: CalendarEvent) => void;
  handleDayClick: (date: DateTime) => void;
  startOfCalendar: DateTime;
  weeks: DateTime[][];
}

export const useEventCalendar = ({
  event,
  onRSVP,
  onCancelRSVP,
  onSelectMeetingTime,
  selectedMeetingTimeId,
  selectedDate,
  onSelectDate,
  currentMonth,
  onMonthChange,
  theme,
}: UseEventCalendarProps): UseEventCalendarReturn => {
  // Auto-select today on mount
  useEffect(() => {
    if (!selectedDate) {
      onSelectDate?.(DateTime.now());
    }
  }, [selectedDate, onSelectDate]);

  // Convert event meeting times to calendar events
  const calendarEvents = useMemo(
    () =>
      event.meetingTimes?.map((meeting) => ({
        color: meeting.userHasRsvpd ? theme.palette.primary.main : theme.palette.grey[300],
        endTime: meeting.endMeetingTime,
        id: meeting.id,
        isSelected: meeting.id === selectedMeetingTimeId,
        isUserAttending: meeting.userHasRsvpd,
        startTime: meeting.beginMeetingTime,
        title: event.name,
      })) ?? [],
    [event.meetingTimes, event.name, theme.palette, selectedMeetingTimeId],
  );

  const handlePrevious = () => {
    onMonthChange(currentMonth.minus({ months: 1 }));
  };

  const handleNext = () => {
    onMonthChange(currentMonth.plus({ months: 1 }));
  };

  const handleToday = () => {
    const today = DateTime.now();
    onMonthChange(today.startOf("month"));
    onSelectDate?.(today);
  };

  const handleEventClick = (e: CalendarEvent) => {
    if (onSelectMeetingTime) {
      onSelectMeetingTime({
        endTime: e.endTime,
        id: e.id,
        startTime: e.startTime,
      });
    }
    if (e.isUserAttending) {
      onCancelRSVP?.(e.id);
    } else {
      onRSVP?.(e.id);
    }
  };

  const handleDayClick = (date: DateTime) => {
    // If clicking the same date
    if (selectedDate?.hasSame(date, "day")) {
      // Find all meeting times for this day
      const dayEvents = calendarEvents.filter((e) => {
        const eventStart = DateTime.fromISO(e.startTime);
        return eventStart.hasSame(date, "day");
      });

      if (dayEvents.length > 0) {
        // Find the current event's index
        const currentIndex = dayEvents.findIndex((e) => e.id === selectedMeetingTimeId);
        // Get the next event (cycle back to first if at end)
        const nextEvent = dayEvents[(currentIndex + 1) % dayEvents.length];

        onSelectMeetingTime?.({
          endTime: nextEvent.endTime,
          id: nextEvent.id,
          startTime: nextEvent.startTime,
        });
      }
    } else {
      // Selecting a new date
      onSelectDate?.(date);

      // If selecting a date in a different month, update the month view
      if (!date.hasSame(currentMonth, "month")) {
        onMonthChange(date.startOf("month"));
      }

      // Find all meeting times for this day
      const dayEvents = calendarEvents.filter((e) => {
        const eventStart = DateTime.fromISO(e.startTime);
        return eventStart.hasSame(date, "day");
      });

      // If there are meeting times for this day, select the first one
      if (dayEvents.length > 0) {
        const firstEvent = dayEvents[0];
        onSelectMeetingTime?.({
          endTime: firstEvent.endTime,
          id: firstEvent.id,
          startTime: firstEvent.startTime,
        });
      } else {
        // No meeting times for this day, clear meeting time selection
        onSelectMeetingTime?.(undefined);
      }
    }
  };

  const startOfMonth = currentMonth.startOf("month");
  // Get the Sunday that precedes the start of the month
  const startOfCalendar = startOfMonth.minus({ days: startOfMonth.weekday % 7 });
  const weeks = Array.from({ length: 6 }, (_, weekIndex) =>
    Array.from({ length: 7 }, (_, dayIndex) => startOfCalendar.plus({ days: weekIndex * 7 + dayIndex })),
  );

  return {
    calendarEvents,
    handleDayClick,
    handleEventClick,
    handleNext,
    handlePrevious,
    handleToday,
    startOfCalendar,
    weeks,
  };
};
